/* eslint-disable react/no-children-prop */
import React, { useMemo, useRef } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import {
  TextWithIndicator,
  _palette,
  Box,
  Grid,
  Typography,
  CustomIcon,
  DropdownMenu
} from '@p81-common/ui';

import createClasses from '../styles';
import { links } from 'data/constants';
import IconButton from '@mui/material/IconButton';
import {
  RightSideBarSubCompProps,
  StatusHistoryItemType,
  StatusHistoryType
} from '../types';
import { useToggle } from 'react-use';
import { runAction } from 'utils/cloudActions';

const indicatorColors = {
  failed: _palette.error[500],
  success: _palette.success[500],
  inProgress: _palette.secondary[500]
};
const BGColors = {
  failed: _palette.error[0],
  success: _palette.success[200],
  inProgress: _palette.secondary[200]
};

export const Value = (props: any) => {
  const styles = createClasses({});
  return <span className={styles.value} {...props} />;
};

export const GatewayBox = (props: RightSideBarSubCompProps) => {
  const { t } = useTranslation(['activity']);
  const {
    gatewayId,
    gatewayImageVersion,
    tunnelId,
    statusFailed,
    customerId,
    setOpenSecondary
  } = props;
  const styles = createClasses({ statusFailed });
  const ref = useRef(null);
  const [menuOpen, toggleMenuOpen] = useToggle(false);

  const handleFailedButtonClick = () => {
    toggleMenuOpen(true);
  };

  const listItems = useMemo(
    () => [
      { name: 'Update', icon: 'refresh' },
      { name: 'Upgrade', icon: 'rocket' },
      { name: 'Rebuild', icon: 'ThreatExtraction' }
    ],
    []
  );

  const actionMapper: { [key: string]: string } = {
    Update: 'gateway-automatic-update',
    Upgrade: 'force-gateway-upgrade',
    Rebuild: 'gateway-rebuild'
  };

  return (
    <Box mb={3} className={styles.gatewayBox}>
      {statusFailed && (
        <>
          <IconButton
            ref={ref}
            className={styles.gatewayFailedButton}
            onClick={handleFailedButtonClick}
          >
            <CustomIcon
              className={styles.gatewayFailedButtonIcon}
              src={`${links.imagesBaseUrl}/icons/icon_3dots.svg`}
            />
          </IconButton>
          {menuOpen && (
            <DropdownMenu
              anchorEl={menuOpen && ref.current}
              arrowSide="topRight"
              open={menuOpen}
              onClose={toggleMenuOpen}
              handleClick={name => () => {
                runAction({
                  action: actionMapper[name],
                  gatewayId: gatewayId,
                  customerId: customerId
                });
                toggleMenuOpen(false);
                setOpenSecondary(false);
              }}
              leftAddition={-85}
              anchorElTopAddition={8}
              listItems={listItems.map(({ name, icon }) => ({
                name,
                textNode: (
                  <div className={styles.menuItem}>
                    <CustomIcon
                      className={styles.menuIcon}
                      src={`${links.imagesBaseUrl}/icons/icon_${icon}.svg`}
                    />
                    <Typography>{name}</Typography>
                  </div>
                )
              }))}
            />
          )}
        </>
      )}
      <Typography className={styles.label}>
        {t('activity:cloudManagementLogs.GATEWAY_ID')}
        <Value>{gatewayId || 'N/A'}</Value>
      </Typography>

      <Typography className={styles.label}>
        {t('activity:cloudManagementLogs.GATEWAY_IMAGE_VERSION')}
        <Value>{gatewayImageVersion || 'N/A'}</Value>
      </Typography>

      {tunnelId && (
        <Typography className={styles.label}>
          {t('activity:cloudManagementLogs.TUNNEL_ID')}
          <Value>{tunnelId || 'N/A'}</Value>
        </Typography>
      )}
      <Grid container flexWrap="nowrap">
        <Typography
          className={styles.label}
          children={t('activity:cloudManagementLogs.STATUS')}
        />
        <TextWithIndicator
          className={styles.indicator}
          children={statusFailed ? 'Finished (failed)' : 'Finished (success)'}
          indicatorColor={
            statusFailed ? indicatorColors.failed : indicatorColors.success
          }
          indicatorShape="circle"
        />
      </Grid>
    </Box>
  );
};

export const ProgressTracking = (data: StatusHistoryType) => {
  const { t } = useTranslation(['activity']);

  const statusHistoryArray = data.statusHistory;

  const reversedStatusHistory = useMemo(() => {
    const res = [];
    if (Array.isArray(statusHistoryArray)) {
      for (let i = statusHistoryArray.length - 1; i >= 0; i--) {
        res.push(statusHistoryArray[i]);
      }
    }
    return res;
  }, [statusHistoryArray]);

  const selectBackgroundColor = (networkBeaconStatus: string) => {
    if (networkBeaconStatus?.includes('success')) {
      return BGColors.success;
    } else if (networkBeaconStatus?.includes('failed')) {
      return BGColors.failed;
    } else {
      return BGColors.inProgress;
    }
  };

  const firstStatusBGColor = selectBackgroundColor(
    reversedStatusHistory[0]?.networkBeaconStatus
  );

  const styles = createClasses({ backgroundColor: firstStatusBGColor });

  const createStatusText = (statusHistory: StatusHistoryItemType) => {
    if (statusHistory?.networkBeaconStatus?.includes('started')) {
      return statusHistory?.networkBeaconStatus;
    }
    if (statusHistory?.networkBeaconStatus?.includes('inprogress')) {
      return 'In Progress ' + statusHistory?.networkBeaconProgress + '%';
    } else {
      return (
        statusHistory?.networkBeaconStatus +
        ' ' +
        statusHistory?.networkBeaconProgress +
        '%'
      );
    }
  };

  const color = (value: string) => {
    if (value?.includes('success')) {
      return indicatorColors.success;
    } else if (value?.includes('failed')) {
      return indicatorColors.failed;
    } else {
      return indicatorColors.inProgress;
    }
  };

  return (
    <Box mb={3}>
      {reversedStatusHistory.map((item: any, i: number) => {
        return (
          <Box
            key={i}
            marginBottom={2}
            className={i === 0 ? styles.firstElementBGColor : ''}
          >
            <Grid container flexWrap="nowrap">
              <Typography
                className={styles.label}
                children={t('activity:cloudManagementLogs.STATUS')}
              />
              <TextWithIndicator
                className={styles.indicator}
                children={createStatusText(item)}
                indicatorColor={color(item?.networkBeaconStatus)!}
                indicatorShape="circle"
              />
            </Grid>

            <Typography className={styles.label}>
              {`${t('activity:cloudManagementLogs.TIME')} `}
              <Value>{moment(item?.updatedAt).utc(false).format('LLL')}</Value>
            </Typography>

            {item?.errorMessage && (
              <>
                <Typography className={styles.errorLabel}>
                  {t('activity:cloudManagementLogs.REASON')}
                </Typography>
                <Value className={styles.label}>{item?.errorMessage}</Value>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
